import React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Tabs - Tabbed interface component
 * 
 * Używa Radix UI Tabs primitive
 * - Rozmiary: "1" | "2"
 * - Keyboard navigation (Arrow keys, Home, End)
 * - ARIA compliant
 * 
 * @example
 * ```tsx
 * <Tabs defaultValue="tab1">
 *   <TabsList>
 *     <TabsTrigger value="tab1">Tab 1</TabsTrigger>
 *     <TabsTrigger value="tab2">Tab 2</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="tab1">Content 1</TabsContent>
 *   <TabsContent value="tab2">Content 2</TabsContent>
 * </Tabs>
 * ```
 */

const tabsListVariants = cva(
  cn(
    "inline-flex items-center justify-center",
    "rounded-[var(--radius-md)]",
    "bg-[var(--dark-a3)]",
    "p-[var(--space-1)]",
    "text-[var(--text-white-85)]"
  ),
  {
    variants: {
      size: {
        "1": "h-9 gap-[var(--space-1)]",
        "2": "h-10 gap-[var(--space-2)]",
      },
    },
    defaultVariants: {
      size: "2",
    },
  }
)

const tabsTriggerVariants = cva(
  cn(
    "inline-flex items-center justify-center whitespace-nowrap",
    "rounded-[var(--radius-base)]",
    "px-[var(--space-4)] py-[var(--space-2)]",
    "text-[var(--font-size-2)] font-[var(--font-weight-medium)]",
    "transition-all duration-150",
    "focus-visible:outline-none focus-visible:ring-2",
    "focus-visible:ring-[var(--pink-9)] focus-visible:ring-offset-2",
    "focus-visible:ring-offset-[var(--dark-1)]",
    "disabled:pointer-events-none disabled:opacity-50",
    "data-[state=active]:bg-[var(--dark-2)]",
    "data-[state=active]:text-[var(--text-white-95)]",
    "data-[state=active]:shadow-sm",
    "hover:bg-[var(--dark-a2)] hover:text-[var(--text-white-95)]"
  ),
  {
    variants: {
      size: {
        "1": "text-[var(--font-size-1)] px-[var(--space-3)]",
        "2": "text-[var(--font-size-2)] px-[var(--space-4)]",
      },
    },
    defaultVariants: {
      size: "2",
    },
  }
)

const Tabs = TabsPrimitive.Root

export interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, size, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(tabsListVariants({ size }), className)}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

export interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof tabsTriggerVariants> {}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, size, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(tabsTriggerVariants({ size }), className)}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-[var(--space-4)]",
      "focus-visible:outline-none focus-visible:ring-2",
      "focus-visible:ring-[var(--pink-9)] focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }

